/* eslint-disable react-hooks/exhaustive-deps */
import { Box, IconButton, InputAdornment, styled, Switch, Tab, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { LoadingPage } from "../../../../common/LoadingPage";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DataTable from "../../../../common/DataTable";
import { apiRequest } from "../../../../common/common";
import { showNotification } from "../../../../common/Notification/ToastifyNotification";
import { userColumn } from "../SoftwareAdmin/ColumnDefs";
import moment from "moment";
import { useSelector } from "react-redux";
import CustomFields from "./CustomFields";

const DemoTabs = styled(Tabs)(({ theme }) => ({
    minWidth: 200,
    borderBottom: '2px solid #e8e8e8',
    [theme.breakpoints.down('xs')]: {
        marginLeft: '0%',
    },
    [theme.breakpoints.down('md')]: {
        minWidth: '20px',
        fontSize: 10,
    },
}));

const DemoTab = styled(Tab)(({ theme }) => ({
    minWidth: 110,
    fontSize: 18,
    textTransform: 'capitalize',
    fontFamily: 'open sans',
    fontWeight: 'bold',
    [theme.breakpoints.down('xs')]: {
        minWidth: 8,
        fontSize: 10,
    },
    '&:hover': {
        backgroundColor: '#f6f7ff',
        color: 'black',
    },
}));


const UserAdminPanel = ({
    isUser
}) => {
    const { roles, user } = useSelector(state => state.users);
    const [activeTab, setActiveTab] = useState(0);
    const [searchingText, setSearchText] = useState(null);
    const [rowsPerPage, setRowPerPage] = useState(10);
    const [gridApi, setGridApi] = useState(null);
    const [dataLoading, setDataLoding] = useState(false);

    const onPaginationChanged = (event) => {
        if (event.api) {
            const newPageSize = event.api.paginationGetPageSize();
            setRowPerPage(newPageSize);
        }
    };

    const onGridReady = useCallback((params) => {
        if (params?.api) {
            const { api, searchingText } = params;
            setGridApi(api);
            setDataLoding(true);
            const dataSource = {
                rowCount: null,
                getRows: async (params) => {
                    const url = [
                        `/org-admin/users?organizationId=${user?.organization?.organization_id}`,
                        `pageNo=${Math.floor((params.startRow / rowsPerPage) + 1)}`,
                        `pageSize=${rowsPerPage}`,
                        searchingText ? `searchText=${encodeURIComponent(searchingText)}` : ''
                    ]
                        .filter(Boolean)  // Filters out empty strings
                        .join('&');
                    try {
                        if (user?.organization?.organization_id) {
                            const response = await apiRequest('get', `${url}`);
                            const { data, total_count } = response.data;
                            let lastRow = -1;
                            if (data?.length <= total_count) {
                                lastRow = total_count;
                            }
                            params.successCallback(data, lastRow);
                        }
                    } catch (error) {
                        setDataLoding(false);
                        showNotification(error?.response?.data.error || error.message, 'error');
                    }
                    setDataLoding(false);
                },
            };
            api.setGridOption("datasource", dataSource);
            setTimeout(() => {
                const node = api.getRenderedNodes();
                if (!node?.[0]?.displayed) {
                    api.showNoRowsOverlay();
                }
            }, 5000);
        }
    }, [rowsPerPage, gridApi]);

    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            if (user?.organization?.organization_id) {
                onGridReady({
                    api: gridApi,
                });
            }
        }
        return () => {
            isMounted = false;
        };
    }, [rowsPerPage, gridApi]);

    const updatedColumns = useMemo(() => {
        let column = userColumn;
        if (column.length > 0) {
            return column.map((col) => {
                if (col.field === 'created_at') {
                    return {
                        ...col,
                        cellRenderer: (params) => (
                            <span>{params?.data?.created_at ? moment(params?.data?.created_at).format('DD-MM-YYYY') : '--'}</span>
                        ),
                    };
                }
                if (col.field === 'phone_number') {
                    return {
                        ...col,
                        cellRenderer: (params) => (
                            <span>{params?.data?.phone_number || '--'}</span>
                        ),
                    };
                }
                if (col.field === 'is_deleted') {
                    return {
                        ...col,
                        cellRenderer: (params) => (
                            <span>{params?.data?.is_deleted ? 'Block' : params?.data?.is_inactive ? 'Temporary Block' : 'Active'}</span>
                        ),
                    };
                }
                if (col.field === 'role') {
                    return {
                        ...col,
                        cellRenderer: (params) => (
                            <span>{params?.data?.role_id ? roles.find((a) => a.id === params?.data?.role_id)?.name : '--'}</span>
                        ),
                    };
                }
                if (col.field === 'name') {
                    return {
                        ...col,
                        cellRenderer: (params) => (
                            <span>{`${params?.data?.first_name} ${params?.data?.last_name || ''}`}</span>
                        ),
                    };
                }
                if (col.field === 'action') {
                    return {
                        ...col,
                        minWidth: 130,
                        width: 130,
                        hide: isUser?.code !== 'OrgAdmin',
                        cellRenderer: (params) => (
                            <Box style={{ display: 'flex', alignItems: 'center' }}>
                                <Tooltip title={params?.data?.is_deleted === false ? 'UnBlock' : 'Block'}>
                                    <Switch
                                        size="small"
                                        disabled={params?.data?.is_inactive === true || roles.find((a) => a.id === params?.data?.role_id)?.code === 'OrgAdmin'}
                                        checked={params?.data?.is_inactive === false}
                                        onChange={async (e) => {
                                            setDataLoding(true);
                                            try {
                                                const response = await apiRequest('put', `/org-admin/user-active-status/${params?.data.user_id}`, {
                                                    is_inactive: e.target.checked === true ? false : true
                                                });
                                                if (response?.data) {
                                                    onGridReady({
                                                        api: gridApi
                                                    })
                                                    setDataLoding(false);
                                                    showNotification(response?.data?.message, 'success');
                                                }
                                            } catch (error) {
                                                setDataLoding(false);
                                                showNotification(error?.response?.data.error || error.message, 'error');
                                            }
                                        }}
                                    />
                                </Tooltip>
                            </Box>
                        ),
                    };
                }
                return col;
            })
        }
        return column;
    }, [userColumn, gridApi, isUser]);

    return (
        <>
            {dataLoading && (<LoadingPage isLoading={dataLoading} />)}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <DemoTabs
                    value={activeTab}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <DemoTab
                        label="Users"
                        value={0}
                        onClick={() => {
                            setActiveTab(0);
                        }} />
                    <DemoTab
                        label="Custom Fields"
                        value={1}
                        onClick={() => {
                            setActiveTab(1);
                        }} />
                </DemoTabs>
            </Box>
            {activeTab === 0 && (
                <>
                    <Box display="flex" justifyContent="space-between" mt={1} mb={1}>
                        <Box>
                            <Typography variant='h4' style={{ fontWeight: 'bold' }}>Users</Typography>
                        </Box>
                        <Box>
                            <TextField
                                size="small"
                                id="outlined-search"
                                type="text"
                                placeholder="Search Users"
                                sx={{
                                    background: '#ebeaea',
                                    marginRight: 1,
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        searchingText && ( // Show the clear icon only if there's text in the field
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => {
                                                        setSearchText('');
                                                        onGridReady({
                                                            api: gridApi,
                                                            searchingText: ''
                                                        });
                                                    }}
                                                >
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    ),
                                }}
                                value={searchingText}
                                onChange={(e) => setSearchText(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        onGridReady({
                                            api: gridApi,
                                            searchingText: e.target.value
                                        });
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                    <DataTable
                        height="75vh"
                        columnDefs={updatedColumns}
                        onGridReady={onGridReady}
                        rowsPerPage={rowsPerPage}
                        paginationPageSizeSelector={[10, 15, 25, 50, 100]}
                        onPaginationChanged={onPaginationChanged}
                        modelType="infinite"
                    />
                </>
            )}
            {activeTab === 1 && (
                <CustomFields />
            )}
        </>
    )
}

export default UserAdminPanel;